/* eslint-disable react/jsx-props-no-spreading */
import { useEffect } from 'react';
import { Button, Col, Placeholder, Row } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Navbar from 'react-bootstrap/Navbar';
import { useDispatch, useSelector } from 'react-redux';
import {
  useNavigate,
  NavLink,
  Link,
  useLocation,
  matchPath,
} from 'react-router-dom';

import classNames from 'classnames';

import '@components/Navbar/Navbar.css';
import styles from '@components/Navbar/Navbar.module.css';

import { IconApi, IconDashboard, IconOrder } from '@components/Icons/Icons';
import { DefaultSpinnerCenter } from '@components/Loader/Loader';
import { LogoLight } from '@components/Logo/Logo';
import NavHoverDropdown from '@components/Navbar/NavHoverDropdown';
import billingSubNav from '@containers/Billing/BillingSubNav';
import capacitySubNav from '@containers/Capacity/CapacitySubNav';
import numbersSubNav from '@containers/Numbers/NumbersSubNav';
import orderSubNav from '@containers/Order/OrderSubNav';
import productSubNav from '@containers/Product/ProductSubNav';
import profilesSubNav from '@containers/Profiles/ProfilesSubNav';
import reportsSubNav from '@containers/Reports/ReportsSubNav';
import ticketsSubNav from '@containers/Tickets/TicketsSubNav';
import userSettingsSubNav from '@containers/UserSettings/UserSettingsSubNav';
import { authApi } from '@reducers/auth';
import { billingApi } from '@reducers/billing';
import { cartApi } from '@reducers/order/order';

const iconClassName = 'd-block mx-auto mb-1 navIcon';
const handleActiveClassName = (isActive) => (isActive ? 'active' : '');

function renderSubNav(data) {
  const dispatch = useDispatch();
  const location = useLocation();
  const handleDisabledLinkClick = (e) => {
    e.preventDefault();
  };
  return (
    <div className={styles.subNav} key={data.title}>
      <Nav.Link
        as={NavLink}
        to={data.href}
        onClick={data.disabledOnClick ? handleDisabledLinkClick : data.onClick}
      >
        <data.icon className={iconClassName} />
        {data.title}
      </Nav.Link>
      <div id={data.navId} className={styles.subNavContent}>
        <Container>
          {data.links.map((item) => {
            const props = {};
            const foundPaths = item.activePaths
              ? item.activePaths.map((path) =>
                  matchPath(path, location.pathname)
                )
              : [];
            if (foundPaths.filter((path) => path).length > 0) {
              props.active = true;
            }
            if (!item.onClick) {
              return (
                <Nav.Link
                  as={NavLink}
                  to={item.href}
                  className={handleActiveClassName}
                  key={item.title + item.href}
                  {...props}
                >
                  {item.title}
                </Nav.Link>
              );
            }
            return (
              <Nav.Link
                key={item.title + item.href}
                onClick={() =>
                  dispatch(item.onClick()) ? item.dispatch : item.onClick
                }
              >
                {item.title}
              </Nav.Link>
            );
          })}
        </Container>
      </div>
    </div>
  );
}

const TopUp = () => {
  const { login } = useSelector((state) => state.auth.user);
  const { data, isLoading, isFetching, isError } =
    billingApi.useGetBalanceQuery({ login });

  if (isLoading || isFetching) {
    return (
      <Navbar.Text as={Row} style={{ minWidth: '145px' }}>
        <Col>
          <Row>
            <Placeholder as={Col} animation="glow">
              <Placeholder.Button size="sm" xs={9} style={{ height: '21px' }} />
            </Placeholder>
          </Row>
          <Row>
            <Placeholder animation="glow">
              <Placeholder xs={9} as="span" />
            </Placeholder>
          </Row>
        </Col>
      </Navbar.Text>
    );
  }

  if (isError) {
    return null;
  }

  return (
    <Navbar.Text as={Row} className="me-1">
      <Col>
        <Row>
          <Button variant="didxl" size="sm" as={Link} to="/billing/balance">
            Top-up
          </Button>
        </Row>
        <Row>Balance: {data?.balance}</Row>
      </Col>
    </Navbar.Text>
  );
};

const UserNavbar = () => {
  const navigate = useNavigate();
  const { data, isLoading, isFetching, isError } =
    cartApi.useGetCheckoutSummaryQuery();
  const {
    login,
    customer_type: customerType,
    is_hijacked: isHijacked,
  } = useSelector((state) => state.auth.user) || '';
  const [triggerRelease, releaseResult] = authApi.useReleaseHijackMutation();
  const isPrepaid = customerType === 1;
  const subNavData = [
    orderSubNav(),
    numbersSubNav(),
    capacitySubNav(),
    profilesSubNav(),
    productSubNav(),
    billingSubNav(),
    reportsSubNav(),
    ticketsSubNav(),
  ];
  const userSettingsSubNavData = [userSettingsSubNav()];
  useEffect(() => {
    if (
      !releaseResult.isUninitialized &&
      !releaseResult.isLoading &&
      !releaseResult.isError
    ) {
      window.location.replace(releaseResult.data.location);
    }
  }, [releaseResult.data]);
  return (
    <>
      {isHijacked ? (
        <Navbar bg="primary" fixed="top">
          <Container fluid>
            <Nav className="me-auto">
              <>
                <Nav.Item className="me-3 d-flex align-items-center">
                  You are currently working on behalf of{' '}
                  <b className="ms-1">{login}</b>.
                </Nav.Item>
                <Nav.Item
                  as={Button}
                  variant="didxl-secondary"
                  size="sm"
                  onClick={triggerRelease}
                  className="ms-1"
                  disabled={releaseResult.isLoading}
                >
                  Release
                </Nav.Item>
              </>
            </Nav>
          </Container>
        </Navbar>
      ) : null}

      <Navbar
        className={classNames({ [styles.mainNavbarHijacked]: isHijacked })}
        bg="didxl"
        expand="sm"
        variant="dark"
        fixed="top"
      >
        <Container>
          <Navbar.Brand href="/">
            <LogoLight className="navbarLogo" />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse
            id="basic-navbar-nav"
            className="justify-content-end"
          >
            <Nav className="me-auto">
              <Nav.Link as={NavLink} to="/" className={handleActiveClassName}>
                <IconDashboard className={iconClassName} />
                Dashboard
              </Nav.Link>
              {subNavData.map((item) => renderSubNav(item))}
              <Nav.Link
                as={NavLink}
                to="/api/settings"
                className={handleActiveClassName}
              >
                <IconApi className={iconClassName} />
                API
              </Nav.Link>
            </Nav>
            <Nav>
              {isPrepaid ? <TopUp /> : null}
              {userSettingsSubNavData.map((item) => renderSubNav(item))}
              <NavHoverDropdown
                title={
                  <div
                    role="button"
                    tabIndex={0}
                    aria-hidden
                    onClick={() => navigate('/cart/checkout')}
                  >
                    <IconOrder className={iconClassName} />{' '}
                    {!isLoading && !isFetching && !isError ? (
                      data.results.filter(({ amount }) => amount !== 'Total')
                        .length
                    ) : (
                      <DefaultSpinnerCenter size="sm" />
                    )}
                  </div>
                }
                id="navbarCartDropdown"
                menuVariant="dark"
                renderMenuOnMount
                className={handleActiveClassName}
              >
                <NavDropdown.Item as={NavLink} to="/cart/cancellation">
                  Cancel
                </NavDropdown.Item>
                <NavDropdown.Divider style={{ borderTopColor: 'gray' }} />
                <NavDropdown.Item as={NavLink} to="/cart/checkout">
                  Checkout
                </NavDropdown.Item>
              </NavHoverDropdown>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </>
  );
};
export default UserNavbar;
